// CreateAccountAfterPayment: Account creation flow after successful payment
// Handles Stripe session verification and creates user account with subscription benefits

import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/config/supabase'
import { CheckCircle, ArrowRight, Loader } from 'lucide-react'

interface CreateAccountAfterPaymentProps {
  sessionId: string
  onAccountCreated: () => void
}

export function CreateAccountAfterPayment({
  sessionId,
  onAccountCreated
}: CreateAccountAfterPaymentProps) {
  const { signUp, loading: authLoading, error: authError } = useAuth()
  const [step, setStep] = useState<'verify' | 'create-account' | 'success'>('verify')
  const [sessionData, setSessionData] = useState<any>(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  })
  const [error, setError] = useState<string | null>(null)
  const [isCreatingAccount, setIsCreatingAccount] = useState(false)

  // Verify Stripe session on mount
  useEffect(() => {
    const verifySession = async () => {
      try {
        // Call our Edge Function to verify the session and get customer data
        console.log('🔍 CreateAccountAfterPayment - Verifying session:', sessionId)
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-payment-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ session_id: sessionId })
        })

        console.log('🔍 CreateAccountAfterPayment - Response status:', response.status)

        if (!response.ok) {
          const errorText = await response.text()
          console.error('🔍 CreateAccountAfterPayment - Error response:', errorText)
          throw new Error(`Failed to verify payment session: ${response.status} ${errorText}`)
        }

        const data = await response.json()
        setSessionData(data)

        // Pre-fill email if available from Stripe
        if (data.customer_email) {
          setFormData(prev => ({ ...prev, email: data.customer_email }))
        }

        setStep('create-account')
      } catch (err) {
        console.error('Session verification failed:', err)
        setError('Unable to verify your payment. Please contact support.')
      }
    }

    if (sessionId) {
      verifySession()
    }
  }, [sessionId])

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setIsCreatingAccount(true)
    setError(null)

    try {
      // Create the account
      const { error: signUpError } = await signUp(
        formData.email,
        formData.password,
        formData.fullName
      )

      if (signUpError) {
        setError(signUpError.message)
        return
      }

      // Get current session to make authenticated requests
      const { data: { session } } = await supabase.auth.getSession()

      if (session && sessionData) {
        // Setup subscription data in user profile
        try {
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/setup-user-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              stripe_customer_id: sessionData.stripe_customer_id,
              stripe_subscription_id: sessionData.stripe_subscription_id,
              plan_name: sessionData.plan_name,
              credits_included: sessionData.credits_included,
              price_id: sessionData.plan_name.toLowerCase(), // Fallback, should be improved
              billing_cycle: sessionData.billing_cycle
            })
          })

          if (!response.ok) {
            console.error('Failed to setup subscription:', await response.text())
            // Don't fail account creation for this - user can contact support
          } else {
            console.log('Subscription setup completed successfully')
          }
        } catch (subscriptionError) {
          console.error('Error setting up subscription:', subscriptionError)
          // Don't fail account creation for this
        }
      }

      // Account created successfully
      setStep('success')

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        onAccountCreated()
      }, 2000)

    } catch (err) {
      setError('Failed to create account. Please try again.')
    } finally {
      setIsCreatingAccount(false)
    }
  }

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Verifying Payment
          </h2>
          <p className="text-gray-600">
            Please wait while we confirm your payment and prepare your account...
          </p>
        </div>
      </div>
    )
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to StockTagger.ai!
          </h2>
          <p className="text-gray-600 mb-6">
            Your account has been created successfully. You'll be redirected to your dashboard shortly.
          </p>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Plan:</strong> {sessionData?.plan_name}<br />
              <strong>Credits:</strong> {sessionData?.credits_included} per month
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-gray-600">
            Create your account to access your {sessionData?.plan_name} plan
          </p>
        </div>

        {/* Account Creation Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleCreateAccount} className="space-y-6">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                required
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your full name"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email address"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                required
                minLength={6}
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Choose a secure password"
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                required
                minLength={6}
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your password"
              />
            </div>

            {/* Error Message */}
            {(error || authError) && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error || authError}</p>
              </div>
            )}

            {/* Plan Summary */}
            {sessionData && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Your Plan</h3>
                <div className="text-sm text-blue-800">
                  <p><strong>{sessionData.plan_name}</strong></p>
                  <p>{sessionData.credits_included} credits per month</p>
                  <p>${(sessionData.amount_paid / 100).toFixed(2)} {sessionData.billing_cycle}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isCreatingAccount || authLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isCreatingAccount || authLoading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account & Start Using StockTagger.ai
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </button>
          </form>

          {/* Terms */}
          <p className="text-xs text-gray-500 text-center mt-4">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  )
}

export default CreateAccountAfterPayment